import { useNotification } from 'src/providers/notification.provider'
import useNotificationContent from './auth-notifications.util'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useMutation } from '@tanstack/react-query'
import { AuthServices } from 'src/services/auth.service'
import { UserService } from 'src/services/user.service'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import {
  TVaidateEmailInitPassword,
  API_ERROR_MESSAGE,
  TRedirectReason,
  TResetPassword,
  TValidateEmail,
  TRequestPin,
  EFormField,
  TLogin,
  ERedirectReason,
} from './auth.type'
import { useSession } from 'src/providers/auth.provider'

export const VALIDATE_EMAIL_INIT_PASSWORD_KEY = 'validate-email-init-password'
export const VALIDATE_EMAIL_KEY = 'validate-email'
export const RESET_PASSWORD_KEY = 'reset-password'
export const REQUEST_PIN_KEY = 'request-pin'

export default function useCredentials() {
  const { login } = useSession()
  const navigate = useLocalizedNavigate()
  const { notify } = useNotification()
  const { t } = useTranslation()
  const NOTIFICATION_CONTENT = useNotificationContent()

  const [formSubmitErrorList, setFormSubmitErrorList] = useState<Array<EFormField>>([])

  const generatePin = (email: string) => {
    return UserService.generateNewPin(email)
  }

  const postLogin = async () => {
    navigate('/auth/select-project', { replace: true })
  }

  const signInUser = async (credentials: TLogin) => {
    try {
      login && (await login(credentials))
      navigate('/auth/select-project', { replace: true })
    } catch {
      throw new Error(t('common.error.generic'))
    }
  }

  const redirectTo = (reason: TRedirectReason, email?: string) => {
    navigate(
      `/auth/password-form?redirectReason=${reason}&email=${email ? encodeURIComponent(email) : ''}`,
    )
  }

  const { mutate: validateEmail } = useMutation({
    mutationKey: [VALIDATE_EMAIL_KEY],
    mutationFn: (form: TValidateEmail) => {
      setFormSubmitErrorList([])
      return UserService.validateEmail(form)
    },
    onSuccess: async (_, sentForm) => {
      notify(NOTIFICATION_CONTENT.VALIDATE_EMAIL.SUCCESS, 3000)
      await signInUser({ email: sentForm.email, password: sentForm.password })
    },
    onError: () => {
      notify(NOTIFICATION_CONTENT.VALIDATE_EMAIL.ERROR, 10000)
    },
  })
  const { mutate: validateEmailInitPassword } = useMutation({
    mutationKey: [VALIDATE_EMAIL_INIT_PASSWORD_KEY],
    mutationFn: (form: TVaidateEmailInitPassword) => {
      setFormSubmitErrorList([])
      return UserService.validateEmailInitPassword(form)
    },
    onSuccess: async (_, sentForm) => {
      notify(NOTIFICATION_CONTENT.VALIDATE_EMAIL_INIT_PASSWORD.SUCCESS, 3000)
      await signInUser({ email: sentForm.email, password: sentForm.newPassword })
    },
    onError: () => {
      notify(NOTIFICATION_CONTENT.VALIDATE_EMAIL_INIT_PASSWORD.ERROR, 10000)
    },
  })
  const { mutate: resetPassword } = useMutation({
    mutationKey: [RESET_PASSWORD_KEY],
    mutationFn: (form: TResetPassword) => {
      setFormSubmitErrorList([])
      return AuthServices.resetPassword(form)
    },
    onSuccess: async (_, sentForm) => {
      notify(NOTIFICATION_CONTENT.RESET_PASSWORD.SUCCESS, 3000)
      await signInUser({ email: sentForm.email, password: sentForm.password })
    },
    onError: () => {
      notify(NOTIFICATION_CONTENT.RESET_PASSWORD.ERROR, 10000)
    },
  })
  const { mutate: requestPin, isPending: isRequestPinLoading } = useMutation({
    mutationKey: [REQUEST_PIN_KEY],
    mutationFn: (form: TRequestPin) => {
      return AuthServices.requestPin(form)
    },
    onSuccess: (_, sentForm) => {
      notify(NOTIFICATION_CONTENT.REQUEST_PIN.SUCCESS, 3000)
      redirectTo(ERedirectReason.RESET_PASSWORD, sentForm.email)
    },
    onError: (error: any, sentForm) => {
      const errorMessage = error.response.data.message
      if (API_ERROR_MESSAGE.VALIDATE_EMAIL_INIT_PASSWORD.includes(errorMessage)) {
        notify(NOTIFICATION_CONTENT.VALIDATE_EMAIL_INIT_PASSWORD.WARNING, 10000)
        redirectTo(ERedirectReason.VALIDATE_EMAIL_INIT_PASSWORD, sentForm.email)
      } else if (API_ERROR_MESSAGE.VALIDATE_EMAIL.includes(errorMessage)) {
        notify(NOTIFICATION_CONTENT.VALIDATE_EMAIL.WARNING, 10000)
        redirectTo(ERedirectReason.VALIDATE_EMAIL, sentForm.email)
      } else if (API_ERROR_MESSAGE.RESET_PASSWORD.includes(errorMessage)) {
        notify(NOTIFICATION_CONTENT.RESET_PASSWORD.WARNING, 10000)
        redirectTo(ERedirectReason.RESET_PASSWORD, sentForm.email)
      } else {
        notify(NOTIFICATION_CONTENT.REQUEST_PIN.ERROR, 10000)
      }
    },
  })

  return {
    validateEmailInitPassword,
    setFormSubmitErrorList,
    isRequestPinLoading,
    formSubmitErrorList,
    validateEmail,
    resetPassword,
    redirectTo,
    requestPin,
    signInUser,
    postLogin,
    generatePin,
  }
}
